import { useEffect } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard, Users, CalendarDays,
  ClipboardCheck, Wallet, FileText,
  Store, MessagesSquare,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { resolveAvatarUrl } from "@/lib/avatar";
import { canAccess, useUserRole, type AppRole } from "@/hooks/use-user-role";
import { useUnreadChats } from "@/hooks/use-unread-chats";
import { useCachedState } from "@/hooks/use-cached-state";
import { cn } from "@/lib/utils";


type Item = { title: string; url: string; icon: React.ComponentType<{ className?: string }> };


// Pinned bottom-nav items per role (max 4 + More).
const PINS: Record<AppRole, Item[]> = {
  superadmin: [
    { title: "Beranda", url: "/dashboard", icon: LayoutDashboard },
    { title: "Siswa", url: "/students", icon: Users },
    { title: "Chat", url: "/chat", icon: MessagesSquare },
    { title: "Store", url: "/store", icon: Store },
  ],
  admin: [
    { title: "Beranda", url: "/dashboard", icon: LayoutDashboard },
    { title: "Siswa", url: "/students", icon: Users },
    { title: "Chat", url: "/chat", icon: MessagesSquare },
    { title: "Store", url: "/store", icon: Store },
  ],
  bendahara: [
    { title: "Beranda", url: "/dashboard", icon: LayoutDashboard },
    { title: "Tagihan", url: "/student-bills", icon: FileText },
    { title: "Chat", url: "/chat", icon: MessagesSquare },
    { title: "Verifikasi", url: "/payments", icon: Wallet },
  ],
  guru: [
    { title: "Beranda", url: "/dashboard", icon: LayoutDashboard },
    { title: "Absensi", url: "/attendance", icon: ClipboardCheck },
    { title: "Chat", url: "/chat", icon: MessagesSquare },
    { title: "Jadwal", url: "/schedules", icon: CalendarDays },
  ],
};

export function MobileBottomNav() {
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const { role } = useUserRole();
  const { total: unreadTotal } = useUnreadChats();
  const [profile, setProfile] = useCachedState<{ name: string; avatar: string | null }>("bottom-nav:profile", { name: "", avatar: null });

  useEffect(() => {
    let cancelled = false;
    const loadProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) {
        if (!user && !cancelled) setProfile({ name: "", avatar: null });
        return;
      }
      const { data: prof } = await supabase
        .from("profiles").select("full_name, avatar_url").eq("id", user.id).maybeSingle();
      const url = await resolveAvatarUrl(prof?.avatar_url);
      if (cancelled) return;
      setProfile({
        name: prof?.full_name || user.email?.split("@")[0] || "Pengguna",
        avatar: url ?? null,
      });
    };
    void loadProfile();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        setProfile({ name: "", avatar: null });
        return;
      }
      if (event === "SIGNED_IN" || event === "USER_UPDATED") void loadProfile();
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const initials = profile.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "U";


  if (!role) return null;
  const pins = PINS[role].filter((i) => canAccess(role, i.url));
  const isActive = (p: string) => pathname === p || pathname.startsWith(p + "/");

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-white"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className="grid grid-cols-5 h-16">
        {pins.map((it) => {
          const active = isActive(it.url);
          const isChat = it.url === "/chat";
          return (
            <li key={it.url} className="flex">
              <Link
                to={it.url}
                resetScroll={false}
                className={cn(
                  "flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium",
                  active ? "text-primary" : "text-muted-foreground"
                )}
              >
                <div className={cn(
                  "relative flex items-center justify-center rounded-full",
                  isChat ? "w-11 h-7" : "w-10 h-6",
                  active && !isChat && "bg-primary/15"
                )}>
                  <it.icon className={cn(isChat ? "w-6 h-6 text-primary" : "w-5 h-5")} />
                  {isChat && unreadTotal > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                      {unreadTotal > 99 ? "99+" : unreadTotal}
                    </span>
                  )}
                </div>
                <span className="truncate max-w-full px-1">{it.title}</span>

              </Link>
            </li>
          );
        })}

        <li className="flex">
          <Link
            to="/profile"
            resetScroll={false}
            className={cn(
              "flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium cursor-pointer transition-colors",
              isActive("/profile") ? "text-primary" : "text-muted-foreground"
            )}
          >
            <Avatar className={cn(
              "w-7 h-7 ring-2 transition-all",
              isActive("/profile") ? "ring-primary" : "ring-transparent"
            )}>
              {profile.avatar && <AvatarImage src={profile.avatar} />}
              <AvatarFallback className="bg-primary/15 text-primary text-[10px] font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <span className="truncate max-w-full px-1">Profil</span>
          </Link>
        </li>
      </ul>
    </nav>
  );
}

